import { Box, formatAmount, Text } from '@bonanza-social/moonlight';
import { memo, useMemo } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { formatDateWithTime } from '@bonanza-social/toolkit/utils';
import { TransactionListItemItem } from '../../state/transactionHistory/transactionHistory.types';

function TransactionHistoryListItem({
  amount,
  createdAt,
  transactionId,
  paymentMethodVendor,
  paymentMethodType,
  paymentMethodDetails,
  status,
  transactionType,
  reason,
}: TransactionListItemItem) {
  const [t] = useTranslation('account');
  const date = useMemo(() => formatDateWithTime(createdAt), [createdAt]);
  const paymentMethodInfo = useMemo(() => {
    return [paymentMethodType, paymentMethodVendor, paymentMethodDetails]
      .filter(Boolean)
      .join(' | ');
  }, [paymentMethodType, paymentMethodVendor, paymentMethodDetails]);

  const formattedAmount = useMemo(() => {
    const award = formatAmount(amount);

    switch (transactionType) {
      case 'Manual Adjustment':
        return amount < 0 ? award : `+${award}`;
      default:
        return award;
    }
  }, [amount, transactionType]);

  return (
    <Box gap="space-300" testID="transaction-history-list-item">
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="body-m" color="darkSurfacePrimary">
          {date}
        </Text>
        <Text variant="body-s" color="darkSurfaceMuted">
          {t('transactionHistoryPaymentId', { paymentId: transactionId })}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="body-m" gradient="darkSurfaceAccent">
          {t(transactionType.replace(/ /g, '_').toLowerCase())}
        </Text>
        <Text variant="h6" color="darkSurfacePrimary">
          {formattedAmount}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="body-s" color="darkSurfaceMuted">
          {paymentMethodInfo}
        </Text>
        <Text variant="body-s" color="darkSurfaceMuted">
          {t(status.toLowerCase())}
        </Text>
      </Box>
      <Box flexDirection="row" justifyContent="flex-end" alignItems="center">
        {reason ? (
          <Text variant="body-s" color="darkSurfaceMuted">
            {reason}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
}

export default memo(TransactionHistoryListItem);
